import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { dersLabel, maarifKazanimMi, kazanimEtiketi, kartBasligi } from '../lib/dersler';
import { BAGLAM_ROZET_SIRA } from '../lib/baglamRozet';
// Puan -> zorluk seviyesi (ad + renk + buton tonu) tek kaynaktan.
import { puanRenk, puanBaslik, puanSeviyeAdi, zorlukSeviye, ZORLUK_SEVIYELERI } from '../lib/zorlukSeviye';
import BenzerlikRozeti from './BenzerlikRozeti';
import { satildiMi, satisRozetMetni, satisRozetBaslik, korUyusmazlikKapali } from '../lib/satis';
import { figurOnculBol } from '../lib/figurOnculBol';
import { pufListesi } from '../lib/pufNoktalari';
import useSesliYazdirma, { metinBirlestir } from '../lib/useSesliYazdirma';
import MikrofonButonu from './ui/MikrofonButonu';
import CevapQcKanit from './CevapQcKanit';
import CevapQcIstek from './CevapQcIstek';

import { API } from '../lib/ortam';

// DB ENUM `red_nedeni` ile senkron tutulmalı.
// Yeni değer ekleyince: (1) DB ENUM'a ALTER TYPE ADD VALUE, (2) buraya etiket ekle.
// Dropdown options aşağıdan otomatik render edilir — manuel <option> EKLEME.
// Mount'ta `/api/sorular/red-nedeni-secenekleri` çağrılır; DB'de yeni ENUM varsa
// dropdown otomatik genişler (etiketsiz değerler key'in kendisiyle gösterilir).
const RED_NEDENI_ETIKET = {
  matematiksel_hata:  'Matematiksel Hata',
  uslup_uyumsuzlugu:  'Üslup Uyumsuzluğu',
  sik_kalitesi:       'Şık Kalitesi Düşük',
  grafik_hatasi:      'Grafik Hatası',
  zorluk_uyumsuzlugu: 'Zorluk Uyumsuzluğu',
  tekrar:             'Tekrar / Benzer Soru',
  mufredat_disi:      'Müfredat Dışı',
  render_fail:        'Görsel Render Hatası',
  diger:              'Diğer',
};
const RED_NEDENI_VARSAYILAN = Object.keys(RED_NEDENI_ETIKET);
const redNedeniEtiket = (v) => RED_NEDENI_ETIKET[v] || v;

// Bracket-matching replace: \textbf{...} içinde $math$ veya iç içe {} varsa
// [^}]* regex'i ilk } görünce keser ve içeriği kaçırır. Bu yardımcı tüm metin
// üzerinden tarayarak dengelenmiş } kapanışını bulur.
function bracketReplace(text, prefix, wrapper) {
  let result = '';
  let i = 0;
  while (i < text.length) {
    const idx = text.indexOf(prefix, i);
    if (idx === -1) { result += text.slice(i); break; }
    result += text.slice(i, idx);
    let depth = 1;
    let j = idx + prefix.length;
    while (j < text.length && depth > 0) {
      if (text[j] === '{') depth++;
      else if (text[j] === '}') depth--;
      j++;
    }
    const inner = text.slice(idx + prefix.length, j - 1);
    result += wrapper(inner);
    i = j;
  }
  return result;
}

// Math bölgelerini atlayarak metin bölgelerini HTML'e dönüştür.
// 2026-05-15 fix: metin bölgelerinde < ve > HTML-escape ediliyor — yoksa
// math olmayan metinde geçen "<" karakteri (örn. çözüm açıklamasında düz
// olarak yazılırsa) innerHTML tarafından HTML tag olarak parse ediliyor;
// "a<b<c" gibi içerik kayboluyordu. Math bölgeleri KaTeX'e dokunulmadan gider.
// HTML tag dönüşümleri (\textbf → <strong>) split'ten önce yapılır ve
// placeholder ile maskelenir, böylece escape onları bozmaz.
// NOT: SoruKarti.test.js bu fonksiyonu import eder (placeholder sentinel \x01
// regresyon guard'ı). `export`'u KALDIRMA — silinirse test import'u kırılır ve
// görünmez sentinel karakteri kazara düşünce (panel render'ı çöker) yakalanamaz.
export function preprocessLatex(text) {
  const placeholders = [];
  const ph = (html) => {
    placeholders.push(html);
    return `${placeholders.length - 1}`;
  };

  // 0. İşaret kaçışı — math/metin ayrımıyla. Math bölgesinde düz < > KaTeX'in
  // tanıdığı \lt / \gt olur: innerHTML "$a<b$" içindeki "<b"yi HTML etiketi
  // sanıp içeriği yemesin (KaTeX'te aynı render). Metin bölgesinde & < > HTML
  // varlığına çevrilir. Satır sonu (\n → <br>) YALNIZ üst düzeyde (adım 3);
  // gövdelerin satır sonu davranışı eskisi gibi kalır.
  // ⛔ AYNI kaçış \underline/\textit/\textbf/** GÖVDESİNE de uygulanır
  // (2026-09-15). Eskiden gövde split'ten ÖNCE placeholder'a alınıyor ve hiçbir
  // kaçıştan geçmeden innerHTML'e gidiyordu: `\textbf{Buna göre $0<x<360$ …}`
  // içindeki "<x<360$ …" bir ETİKET açıyor, gövdenin kalanı siliniyordu (canlı
  // #7170 panelde "Buna göre $0"; 8.423 satırlık ölçümde 6 onaylı soru).
  const kacir = (s, satirSonu) => s.split(
    /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$]+?\$|\\\([\s\S]+?\\\))/
  ).map((part, i) => {
    if (i % 2 === 1) return part.replace(/</g, '\\lt ').replace(/>/g, '\\gt ');
    const metin = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return satirSonu ? metin.replace(/\\n/g, '<br>').replace(/\n/g, '<br>') : metin;
  }).join('');

  // 1. LaTeX metin komutlarını HTML tag'e dönüştür — placeholder ile koru
  // Neden önce: \textbf{Buna göre, $k=\dfrac{a}{b}$ ...} gibi içinde math olan
  // \textbf{} varsa split sonrası [^}]* regex iç kısmı kaçırır (B1 fix, Faz 4).
  text = bracketReplace(text, '\\underline{', (s) => ph(`<u>${kacir(s, false)}</u>`));
  text = bracketReplace(text, '\\textit{',    (s) => ph(`<em>${kacir(s, false)}</em>`));
  text = bracketReplace(text, '\\textbf{',    (s) => ph(`<strong>${kacir(s, false)}</strong>`));

  // 2. Markdown bold geri uyumluluk: **...** → <strong>
  text = text.replace(/\*\*([^*\n]+?)\*\*/g, (m, p1) => ph(`<strong>${kacir(p1, false)}</strong>`));

  // 3. Üst düzey: math bölgelerini ayır; metinde HTML-escape + newline
  let out = kacir(text, true);

  // 4. Placeholder'ları geri koy (HTML tag'ler şimdi escape edilmiş metnin
  // arasına eklenir — math bölgesi içinde değiller, KaTeX renderMathInElement
  // DOM dolaşır, math'i <strong> içinde de bulup render eder)
  // Ic-ice placeholder (orn. <strong> icinde <u> token) icin DONGU;
  // sentinel sayesinde metindeki duz sayilar dokunulmaz.
  let _prev;
  do {
    _prev = out;
    out = out.replace(/(\d+)/g, (_, idx) => placeholders[parseInt(idx, 10)]);
  } while (out !== _prev && /\d+/.test(out));

  return out;
}

// Liste kartı önizlemesi (backend LEFT(soru_metni_latex, 400), 2026-10-20): kesim bir $...$
// çiftini bölmüşse son tek $'dan sonrası atılır (yoksa KaTeX ham metin basar); satır sonları
// boşluk olur. Kesilmiş metin … ile biter.
export function onizlemeKes(ham, sinir = 400) {
  const kaynak = String(ham || '');
  let t = kaynak.replace(/\s+/g, ' ').trim();
  if ((t.match(/(?<!\\)\$/g) || []).length % 2 === 1) t = t.slice(0, t.lastIndexOf('$')).trimEnd();
  return kaynak.length >= sinir ? `${t}…` : t;
}

export function LatexText({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = preprocessLatex(children || '');
    if (window.renderMathInElement) {
      window.renderMathInElement(ref.current, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$',  right: '$',  display: false },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        throwOnError: false,
      });
    }
  }, [children]);
  return <span ref={ref} />;
}

// LatexParagraphs — coksatirli soru metnini (\n+ ile ayrik) AYRI <p> bloklarina
// boler ve aralarina space-y-3 (12px) bosluk koyar.
//
// NEDEN VAR (SILME / LatexText'e GERI CEVIRME / space-y-3'u DUSURME):
//   Ogretmen #12499 (2026-07-08) + #14081 (2026-07-14): "I, II, III maddelerin
//   satir araliklari cok yakin, okunmuyor". LatexText metni \n -> <br> ile TEK
//   akan blokta diziyor; oradaki maddeler arasi bosluk OLCULDU: yalnizca 1.6px
//   ve 3.1px (uzun matematik yaninda goze yapisik goruunuyor). #14081'de I.
//   maddedeki \dfrac 42.1px yuksekliginde, digerleri 23-28px.
//   -> Ayri <p> + acik bosluk ile 13.6px / 15.1px'e cikti (2026-07-14 olcumu).
//
//   DIKKAT: Ayri <p>'ye bolmek TEK BASINA bosluk KAZANDIRMAZ (olculdu: <br> ile
//   ayni geometri). Kazanc space-y-3'ten gelir — o sinifi dusurursen hata geri
//   doner. <p>'ye m-0 EKLEME: space-y (0,3,0) m-0'i (0,1,0) yener ama gereksiz
//   ve yaniltici; Tailwind preflight zaten p{margin:0} veriyor.
//
//   Bu duzeltme 2026-07-08'de eklenmis, 2026-07-12 d01e9d8 refactor'unde
//   YANLISLIKLA LatexText'e geri cevrilmis (regresyon). SoruKarti.test.js
//   "LatexParagraphs — coksatirli blok koruması" bileseni + cok-<p> davranisini
//   korur; silme/geri cevirme testi dusurur.
// ⛔ SATIR SONU ICEREN MATH BLOGU BOLUNEMEZ (2026-08-12, ogretmen #25904/25908/25916)
//   Uretim cok satirli display-math yaziyor:
//       ... kalite skoru
//       \[
//       K(x)=x(60-x)^{2}
//       \]
//       formuluyle hesaplanmaktadir.
//   Duz `split(/\n+/)` bunu 5 ayri <p>'ye dagitiyordu; `\[` ile `\]` AYRI
//   LatexText'lere dusuyor, delimiter cifti kopuyor ve panel `\[`, ham formul,
//   `\]` diye HAM basiyordu. Alan-butunune bakan dedektorler (latex_panel_lint,
//   latex_render_denetim) cifti SAGLAM gordugu icin bunu yakalayamaz — hata
//   bolucudedir. Cozum: bolmeden ONCE math bloklarindaki `\n`i maskele.
export function paragraflaraBol(ham) {
  const NL = '\u0002';  // gorunur kacis — ham kontrol karakteri yazma (dosya basindaki uyari)
  const korunmus = (ham || '').replace(
    /\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)/g,
    (blok) => blok.split('\n').join(NL)
  );
  return korunmus
    .split(/\n+/)
    .map(c => c.split(NL).join('\n').trim())
    .filter(Boolean);
}

export function LatexParagraphs({ children, className = '' }) {
  const paragraflar = paragraflaraBol(children);
  if (paragraflar.length === 0) return null;
  return (
    <div className={`space-y-3 ${className}`}>
      {paragraflar.map((cumle, i) => (
        <p key={i} className="leading-7"><LatexText>{cumle}</LatexText></p>
      ))}
    </div>
  );
}

export function TikzDiagram({ tikzCode }) {
  const [svg, setSvg] = useState(null);
  const [hata, setHata] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hamGoster, setHamGoster] = useState(false);

  useEffect(() => {
    if (!tikzCode) return;
    let cancelled = false;
    setYukleniyor(true); setHata(null);
    fetch(`${API}/api/tikz/render`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tikz: tikzCode }) })
      .then(r => r.json())
      .then(data => { if (!cancelled) { data.basarili ? setSvg(data.svg) : setHata(data.hata || 'Render basarisiz'); } })
      .catch(e => { if (!cancelled) setHata(e.message); })
      .finally(() => { if (!cancelled) setYukleniyor(false); });
    return () => { cancelled = true; };
  }, [tikzCode]);

  return (
    <div className="my-3">
      <div className="flex justify-end mb-1">
        <button onClick={() => setHamGoster(!hamGoster)} className="px-3 py-1 rounded border border-gray-300 bg-white text-xs cursor-pointer hover:bg-gray-50">
          {hamGoster ? 'Görsel' : 'TikZ Kodu'}
        </button>
      </div>
      {hamGoster ? (
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto font-mono">{tikzCode}</pre>
      ) : yukleniyor ? (
        <div className="text-center py-6 text-gray-500 font-serif">Diyagram derleniyor...</div>
      ) : hata ? (
        <div className="p-3">
          <div className="text-orange-600 mb-2">Diyagram derlenemedi: {hata}</div>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto font-mono">{tikzCode}</pre>
        </div>
      ) : svg ? (
        <div className="text-center py-2 overflow-auto max-w-full" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto font-mono">{tikzCode}</pre>
      )}
    </div>
  );
}

// Raster görsel (PNG/JPEG) render — biyoloji görsel-çapaları/görsel-leaf'leri için (FAZ9).
// gorsel: tam URL (http...), data-URI (data:image...) veya çıplak base64 (image/png varsayılır).
export function GorselDiagram({ gorsel }) {
  if (!gorsel) return null;
  const src = /^(https?:|data:)/i.test(gorsel) ? gorsel : `data:image/png;base64,${gorsel}`;
  return (
    // Boyut siniri (ogretmen 2026-08-12 "sekli kucult biraz"): SVG `gorsel_url`
    // kayitlarinda `width`/`height` YOK, yalniz viewBox var -> <img> kapsayicinin
    // TAM genisligine yayilir (max-w-3xl = 768px -> 480x350 izgara ~560px yuksek).
    // max-h ile oran korunarak kucultulur; TikZ figurleri texlive'dan acik pt
    // olculeriyle geldigi icin bu yoldan ETKILENMEZ.
    <div className="my-3 text-center py-2 overflow-auto max-w-full">
      <img src={src} alt="Soru görseli" className="inline-block max-w-full h-auto max-h-[360px] rounded border border-gray-200" />
    </div>
  );
}

export const SIK_HARFLERI = ['A', 'B', 'C', 'D', 'E'];

// Şıkkın EKRANDA kapladığı kaba yer (karakter sayısı DEĞİL).
// `$` ve süslü parantez yer kaplamaz; `\dfrac`/`\cup` gibi bir LaTeX komutu
// tek simge basar — ham `.length` bunları 5-6 karakter sayıp kısa bir şıkkı
// uzun sanır ve ızgarayı gereksiz kapatırdı.
export function kabaSikUzunlugu(t) {
  return String(t == null ? '' : t)
    .replace(/\$/g, '')
    .replace(/\\[a-zA-Z]+\s*/g, 'x')   // \dfrac, \cup, \sqrt ... -> tek simge
    .replace(/\\[\s,;!:]/g, '')        // \, \; \! ince boşluklar -> hiç
    .replace(/[{}]/g, '')
    .trim().length;
}

export const SIK_IZGARA_ESIGI = 30;

/**
 * Şıklar iki sütunlu ızgaraya uygun mu? (öğretmen/yayınevi geri bildirimi 2026-08-18)
 *
 * ÖSYM kitapçığında KISA şıklar yan yana dizilir — vitrin görünümünü asıl veren
 * budur. Ama kural BLANKET uygulanamaz: sözel derslerde şıklar cümle uzunluğunda
 * ve iki sütuna sıkışınca okunaksızlaşır (23.011 sorunun çoğu Türkçe/sosyal/
 * edebiyat). Bu yüzden karar içeriğe bakar: yalnız BEŞ şıkkın hepsi eşiğin
 * altındaysa ızgara açılır, aksi halde bugünkü tek sütun aynen korunur.
 *
 * Eksik şık (boş/null) -> tek sütun: ızgarada boş hücre delik gibi görünür.
 * İstisna: E boşsa soru 4 şıklıdır (LGS, sik_e NULL) -> A–D 2+2 ızgaraya bakılır.
 */
export function sikIzgaraUygunMu(soru) {
  const tum = SIK_HARFLERI.map((h) => String((soru && soru[`sik_${h.toLowerCase()}`]) ?? '').trim());
  const metinler = tum[4] ? tum : tum.slice(0, 4);
  if (metinler.some((m) => !m)) return false;
  return metinler.every((m) => kabaSikUzunlugu(m) <= SIK_IZGARA_ESIGI);
}

// Bağlam temelli grup üyesinin gövdesinden ORTAK KÖK'ü kırpar (KURALLAR §19.3).
// Kök DB'de her üyede TEKRAR eder (saklama sözleşmesi — PDF/video/dışa aktarım
// gibi grubu okumayan kanallar soruyu bağlamsız göstermesin diye); birleşik kart
// onu grup başına BİR KEZ gösterir, üyelerde kırpar.
//
// Karşılaştırma boşluğa duyarsız: builder/import arasında "\n\n" ↔ "\n" ↔ "  "
// farkı gerçek bir uyumsuzluk değil. Prefix EŞLEŞMEZSE metin OLDUĞU GİBİ döner
// (güvenli düşüş — yanlış kırpıp soruyu sakatlamaktansa kökü iki kez göster).
export function kokKirp(metin, ortakKok) {
  if (!metin || !ortakKok) return metin || '';
  const bosluk = (c) => /\s/.test(c);
  let i = 0;
  let j = 0;
  while (i < metin.length && j < ortakKok.length) {
    if (bosluk(metin[i]) && bosluk(ortakKok[j])) {
      while (i < metin.length && bosluk(metin[i])) i++;
      while (j < ortakKok.length && bosluk(ortakKok[j])) j++;
      continue;
    }
    if (metin[i] !== ortakKok[j]) return metin;   // eşleşmedi
    i++;
    j++;
  }
  while (j < ortakKok.length && bosluk(ortakKok[j])) j++;
  if (j < ortakKok.length) return metin;          // kök metinden uzun
  const kalan = metin.slice(i).replace(/^\s+/, '');
  return kalan || metin;                          // kırpınca hiçbir şey kalmıyorsa dokunma
}

// `grup` prop'u = { sira, toplam, ortakKok } — BaglamGrupKarti sarmalayıcısı verir.
// Grup modunda: "Geri Dön" gizli (sarmalayıcı bir kez basar), ortak kök gövdeden
// kırpılır, görsel gizlenir (sarmalayıcı bir kez gösterir). Puan/onay/red/pratik/
// video blokları AYNEN çalışır — onay ve puan KAYIT BAŞINADIR (§19.3 md.6).
export default function SoruKarti({ soru, onOnayla, onPuanKaydet, onPratikOnayla, onVideoOnayla, onKorCevap, onKorPuan, onCevapQcKanit, onCevapQcIstek, onGeri, grup = null }) {
  const [yorum, setYorum] = useState('');
  const [puan, setPuan] = useState(0);
  const [redNedeni, setRedNedeni] = useState('');
  const [redNedeniOptions, setRedNedeniOptions] = useState(RED_NEDENI_VARSAYILAN);
  const [sistemAcik, setSistemAcik] = useState(false);
  // 2026-07-29 (ogretmen #18555-18557 "uzun cozumu kimse okumaz"): pratik cozum
  // varsa detayli cozum varsayilan KAPALI (katlanabilir); pratik yoksa eski gorunum.
  const [detayCozumAcik, setDetayCozumAcik] = useState(false);
  // KÖR ONAY (2026-09-15, cevap QC — kullanıcı kararı "kanıt + örneklemeli kör onay"):
  // örneklemdeki kartta anahtar, çözüm, pratik ve QC kanıtı öğretmen cevabını
  // işaretleyene kadar gizlidir. `korSonuc` sunucunun döndürdüğü {cevap, uyustu, dogru_cevap}.
  const [korSonuc, setKorSonuc] = useState(null);
  const [korHata, setKorHata] = useState('');
  const [korGonderiliyor, setKorGonderiliyor] = useState(false);
  // ZORLUK KÖR PUANI (2026-09-25, kullanıcı kararı "yalnız ölçüm"): ajanın puanladığı sorudan
  // örneklenen kartta ajan puanı/yorumu sunucuda gizlidir; öğretmen puanını "Kör puanı kaydet"
  // ile verir (denetim_izi, zorluk_puan DEĞİŞMEZ). `korPuanSonuc` = {puan, ajan_puan}.
  const [korPuanSonuc, setKorPuanSonuc] = useState(null);
  const [korPuanHata, setKorPuanHata] = useState('');
  const [korPuanGonderiliyor, setKorPuanGonderiliyor] = useState(false);
  const s = soru;

  useEffect(() => {
    setPuan(soru?.zorluk_puan ?? 0);
    // Yorum kutusu yalniz ogretmenin kendi metnini gosterir; AI/sistem notlari
    // ayri blokta (asagida). Fallback: backend eski surumdeyse ham metin.
    setYorum(soru?.ogretmen_yorumu_temiz ?? soru?.ogretmen_yorumu ?? '');
    setRedNedeni(soru?.red_nedeni ?? '');
    setSistemAcik(false);
    setDetayCozumAcik(false);
    setKorSonuc(null);
    setKorHata('');
    setKorPuanSonuc(null);
    setKorPuanHata('');
  }, [soru?.id]);

  // Kör mod yalnız cevap alınabiliyorsa açılır: `onKorCevap` verilmeyen bir yerde
  // (eski ekran) açılsaydı "Onayla" hiç açılmayacak biçimde kilitlenirdi.
  const korMod = !!onKorCevap && !!s.kor_ornek && !s.kor_onay && !korSonuc;
  const korBilgi = korSonuc || s.kor_onay || null;
  const korPuanMod = !!onKorPuan && !!s.kor_puan_ornek && !korPuanSonuc;
  const korPuanBilgi = korPuanSonuc || s.kor_puan || null;
  const korPuanKaydet = async () => {
    if (!onKorPuan || korPuanGonderiliyor || !puan) return;
    setKorPuanGonderiliyor(true);
    setKorPuanHata('');
    try {
      setKorPuanSonuc((await onKorPuan(s, puan)) || { puan });
    } catch (e) {
      setKorPuanHata((e && e.message) || 'Kör puan kaydedilemedi');
    } finally {
      setKorPuanGonderiliyor(false);
    }
  };
  const korCevapla = async (harf) => {
    if (!onKorCevap || korGonderiliyor) return;
    setKorGonderiliyor(true);
    setKorHata('');
    try {
      const r = await onKorCevap(s, harf);
      setKorSonuc(r && r.cevap ? r : { cevap: harf, uyustu: harf === s.dogru_cevap });
    } catch (e) {
      setKorHata((e && e.message) || 'Kör cevap kaydedilemedi');
    } finally {
      setKorGonderiliyor(false);
    }
  };

  // ── SESLİ YAZDIRMA (dikte) ────────────────────────────────────────────────
  const yorumRef = useRef(null);
  const sesDurumId = useId();

  // (b) BAYAT CLOSURE: tanıma callback'i kurulum anındaki `yorum`u kapatır.
  // `setYorum(metinBirlestir(yorum, p))` yazsaydık öğretmenin dikte SIRASINDA
  // klavyeyle eklediği/sildiği metin geri gelirdi. Fonksiyonel güncelleme ŞART.
  const kesinMetinEkle = useCallback((parca) => {
    setYorum(prev => metinBirlestir(prev, parca));
    // Kutu min-h-[80px]; uzun dikte alta taşar → yeni metni görünür tut.
    const el = yorumRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, []);

  // (d) `anahtar={soru?.id}` — soru değişince dikte OTOMATİK durur; bir sorunun
  // sesi diğerinin yorum kutusuna sızmaz. Kural hook'un içinde, burada değil;
  // yukarıdaki sıfırlama effect'ine dokunulmadı.
  const ses = useSesliYazdirma({ anahtar: soru?.id, onKesinMetin: kesinMetinEkle });

  // Klavye: yorum kutusu VEYA mikrofon butonu odaktayken Ctrl+M aç/kapa, Esc
  // durdurur. GLOBAL dinleyici YOK — bağlam grubunda sayfada birden çok yorum
  // kutusu var, global kısayol hangisine ait olduğunu bilemez.
  // (Ctrl+Shift+M KULLANILMAZ: Chrome'da profil değiştirici.)
  const sesTusu = (e) => {
    if (e.ctrlKey && !e.shiftKey && !e.altKey && (e.key === 'm' || e.key === 'M')) {
      e.preventDefault();
      ses.degistir();
    } else if (e.key === 'Escape' && ses.dinliyor) {
      e.preventDefault();
      ses.durdur();
    }
  };

  // adim_sayisi kolonu izole tablolarda (or. sorular_maarif) hep NULL — metindeki
  // "Adım N:" bloklarindan turet (rozet + Detaylar icin hafif fallback).
  const adimSayisiG = s.adim_sayisi
    || ((s.cozum_adimlari_latex || '').match(/Ad[ıi]m\s*\d+\s*:/g) || []).length
    || null;
  const pratikVar = !!s.pratik_cozum;
  const cozumGoster = !pratikVar || detayCozumAcik;

  // Grup gorunumunde ALINMIS eylemin butonu pasiflesir (ogretmen 2026-08-16):
  // baglam grubunda 1. soru onaylandiktan sonra "hangi soru kaldi" belli olmuyordu.
  // TEKIL gorunume UYGULANMAZ — tekil akis zaten dogru calisiyor (onay -> aninda
  // listeye doner), dokunulmaz. `★ Puan Kaydet` her iki modda da ACIK kalir:
  // onaylanmis uyenin puani hala degistirilebilmeli (puanKaydet yonlendirme yapmaz).
  const onayKilitli = !!grup && s.durum === 'onaylandi';
  const redKilitli = !!grup && s.durum === 'reddedildi';

  // DB ENUM'unu mount'ta çek — backend `red_nedeni` ENUM'una yeni değer eklenince
  // frontend rebuild gerekmeden dropdown otomatik genişler.
  useEffect(() => {
    const token = localStorage.getItem('yks_token');
    if (!token) return;
    fetch(`${API}/api/sorular/red-nedeni-secenekleri`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data?.secenekler) && data.secenekler.length > 0) setRedNedeniOptions(data.secenekler); })
      .catch(() => { /* fallback varsayılan listeye */ });
  }, []);

  return (
    <div className={grup ? 'font-serif' : 'max-w-3xl mx-auto font-serif'}>
      {!grup && (
        <button onClick={onGeri} className="px-4 py-2 rounded-lg border border-gray-400 bg-white cursor-pointer mb-4 text-sm hover:bg-gray-50">← Geri Dön</button>
      )}

      {/* BASLIK */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          {grup && (
            <span className={`${BAGLAM_ROZET_SIRA} mr-2 align-middle`}
                  title={`Bağlam temelli grubun ${grup.sira}. sorusu (toplam ${grup.toplam})`}>
              Soru {grup.sira}/{grup.toplam}
            </span>
          )}
          {s.public_no != null && (
            <span className="inline-block text-white px-2 py-0.5 rounded-md text-sm font-bold mr-2 align-middle" style={{backgroundColor: '#111827'}} title="Soru numarasi (panoya kopyala)">#{s.public_no}</span>
          )}
          {/* SATIŞ ROZETİ (2026-09-14): satılmış soru (münhasır satış). yayinevi/tarih
              yalnız admin'e gelir; yoksa "Satıldı". Tarih title'da. */}
          {satildiMi(s.satis) && (
            <span data-satis-rozet className="inline-block text-white px-2 py-0.5 rounded-md text-sm font-bold mr-2 align-middle bg-emerald-600"
                  title={satisRozetBaslik(s.satis)}>
              {satisRozetMetni(s.satis)}
            </span>
          )}
          {!grup && kartBasligi(s)}
        </h2>
        <div className="flex gap-2">
          <span className="text-white px-2.5 py-1 rounded text-xs font-semibold" style={{backgroundColor: '#1976D2'}}>{dersLabel(s.ders)}</span>
          {maarifKazanimMi(s.kazanim_kodu) && (
            <span className="text-white px-2.5 py-1 rounded text-xs font-bold" style={{backgroundColor: '#059669'}} title={`Maarif 2024 müfredatı — ${hedefSinav(s.ders)} hedefli`}>MAARİF</span>
          )}
          {korPuanMod
            ? <span data-kor-puan-rozet className="px-2.5 py-1 rounded text-xs font-semibold border border-amber-400 text-amber-800 bg-amber-50" title="Kör puan örneği — ajanın puanı gizli">🙈 Kör puan</span>
            : s.zorluk_puan != null
            ? <span className="text-white px-2.5 py-1 rounded text-xs font-semibold" title={puanBaslik(s.zorluk_puan)} style={{backgroundColor: puanRenk(s.zorluk_puan)}}>Puan {s.zorluk_puan}/10 · {puanSeviyeAdi(s.zorluk_puan)}</span>
            : <span className="px-2.5 py-1 rounded text-xs font-semibold border border-gray-300 text-gray-500 bg-white">Puan yok</span>
          }
          <span className="text-white px-2.5 py-1 rounded text-xs font-semibold" style={{backgroundColor: durumRenk(s.durum)}}>{durumEtiket(s.durum)}</span>
          {/* BENZERLIK ROZETI (P1a, benzeşim denetimi 2026-08-13): korpustaki en
              yakın eşi >= 0.90 kosinüs olan sorularda görünür; tıklayınca eş soru
              `?public_no=N` ile filtrelenir. Tüm ders tablolarında çalışır. */}
          <BenzerlikRozeti soru={s} />
        </div>
      </div>

      {/* DUSUK GUVEN UYARISI */}
      {s.zorluk_guven && parseFloat(s.zorluk_guven) < 0.6 && (
        <div className="bg-orange-50 border-2 border-orange-400 rounded-lg px-4 py-2.5 mb-3 text-sm text-orange-800">
          <strong>Düşük Zorluk Güveni ({parseFloat(s.zorluk_guven).toFixed(2)})</strong> — Zorluk seviyesi öğretmen tarafından doğrulanmalıdır.
        </div>
      )}

      {/* KAZANIM + ALT KONU + MUFREDAT */}
      {(s.kazanim_kodu || s.alt_konu) && (
        <div className="flex gap-2 mb-3 flex-wrap">
          {s.kazanim_kodu && <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[13px] font-semibold">{s.kazanim_kodu}</span>}
          {mufredatEtiketi(s.kazanim_kodu, s.ders)}
          {s.alt_konu && <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-[13px] font-semibold">{s.alt_konu}</span>}
        </div>
      )}
      {s.kazanim_adi && (
        <div className="text-sm text-gray-700 mb-3">
          <span className="font-semibold text-gray-500">{kazanimEtiketi(s)}:</span> {s.kazanim_adi}
        </div>
      )}

      {/* SORU METNI — B4: kapanış cümlesi (\textbf{Buna göre,...}) ayrı div + margin
          (diyagram varsa diyagramdan SONRA, yoksa giriş paragrafından SONRA satır başı) */}
      <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
        <h3 className="text-base font-semibold text-blue-700 mt-0 mb-3 border-b border-gray-100 pb-2">Soru</h3>
        {(() => {
          // Grup modunda ortak kök gövdeden kırpılır (sarmalayıcı bir kez gösterdi);
          // görsel de köke aittir (§19.3 md.7) → üyede tekrar basılmaz.
          const metin = grup
            ? kokKirp(s.soru_metni_latex || '', grup.ortakKok)
            : (s.soru_metni_latex || '');
          const hasDiyagram = !grup && !!s.diyagram_tikz;
          const hasGorsel = !grup && !!(s.gorsel_base64 || s.gorsel_url);
          const gorselDeger = s.gorsel_base64 || s.gorsel_url;
          const gorselVar = hasDiyagram || hasGorsel;
          // Kapanış: \textbf{Buna göre,...} VEYA düz satır-başı "Buna göre,.../Bu durumda,.../Verilenlere göre,..."
          const kapanis_re = /(?:\\textbf\s*\{\s*|(?:^|\n)[ \t]*)(?:Buna\s+g[oö]re|Bu\s+durumda|Verilenlere\s+g[oö]re)[^]*$/i;
          const km = kapanis_re.exec(metin);
          let kapanis = km ? km[0].replace(/^\s+/, '') : '';
          let giris = km ? metin.slice(0, km.index).trimEnd() : metin;
          // İBARE-DIŞI KAPANIŞ (2026-08-11): ÖSYM kapanışları her zaman
          // "Buna göre/Bu durumda/Verilenlere göre" ile başlamaz —
          // "Bu tabloya göre…", "Bu gözlemlere göre…", "Bu ürünlere göre…",
          // "Aşağıdaki eşleştirmelerden hangisi…" de geçerli kapanıştır
          // (85 tablolu soruda 19'u bu biçimde). İbare listesi genişletmek
          // yerine YAPISAL ölçüt: metnin EN SONUNDAKİ kalın blok bir SORU
          // cümlesiyse kapanıştır. Blok sonda değilse (öncüllerden önce
          // geliyorsa) dokunulmaz — öncüller kapanışın içine yutulmasın.
          if (!km) {
            const bas = metin.lastIndexOf('\\textbf{');
            if (bas !== -1) {
              let derinlik = 1, j = bas + 8;
              while (j < metin.length && derinlik > 0) {
                if (metin[j] === '{') derinlik++;
                else if (metin[j] === '}') derinlik--;
                j++;
              }
              const ic = metin.slice(bas + 8, j - 1);
              if (derinlik === 0 && metin.slice(j).trim() === '' && /\?\s*$/.test(ic)) {
                kapanis = metin.slice(bas).trim();
                giris = metin.slice(0, bas).trimEnd();
              }
            }
          }
          // ÖNCÜL bloğu (I. ... ya da • maddeleri) grafikten SONRA — öğretmen talimatı
          // 2026-07-14 (#13866) + GRAFIK_EKSEN_STANDARDI v2.0 §14 (2026-09-26): bağlam →
          // görsel → (açış / ":" giriş cümlesi) → öncüller. Basımla İKİZ: lib/figurOnculBol.
          let girisUst = giris, girisAlt = '';
          const bolme = gorselVar ? figurOnculBol(giris) : null;
          if (bolme) {
            girisUst = bolme.ust;
            girisAlt = bolme.alt;
          }
          // Render: bağlam → grafik → öncüller → kapanış (grafik yoksa öncüller ayrılmaz)
          return (
            <>
              {girisUst && <LatexParagraphs className="text-base text-gray-800 mb-2">{girisUst}</LatexParagraphs>}
              {hasDiyagram && <TikzDiagram tikzCode={s.diyagram_tikz} />}
              {hasGorsel && <GorselDiagram gorsel={gorselDeger} />}
              {girisAlt && <LatexParagraphs className="text-base text-gray-800 mt-3">{girisAlt}</LatexParagraphs>}
              {kapanis && <LatexParagraphs className="text-base text-gray-800 mt-2 mb-4">{kapanis}</LatexParagraphs>}
            </>
          );
        })()}
        {/* ŞIKLAR — kısa şıklarda iki sütunlu ÖSYM dizilişi, uzun/sözel şıklarda
            tek sütun (bkz. sikIzgaraUygunMu). Boş şık gösterilmez (LGS 4 şık, sik_e
            NULL). Tek sayıda şıkta son şık yalnız kalmasın diye iki sütunu kaplar. */}
        {(() => {
          const izgara = sikIzgaraUygunMu(s);
          const harfler = SIK_HARFLERI.filter((h) => String(s[`sik_${h.toLowerCase()}`] ?? '').trim());
          return (
            <div className={izgara ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'flex flex-col gap-2'}>
              {harfler.map((h, i) => {
                // Kör modda anahtar işaretlenmez; şık, öğretmenin kör cevabını alan düğmedir.
                const dogru = !korMod && s.dogru_cevap === h;
                const korSecim = !!korBilgi && korBilgi.cevap === h;
                const sonTek = izgara && harfler.length % 2 === 1 && i === harfler.length - 1;
                const sinif = `px-4 py-2.5 rounded-lg text-[15px] font-serif min-w-0 break-words${sonTek ? ' sm:col-span-2' : ''} ${dogru ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border border-gray-100'}`;
                if (korMod) {
                  return (
                    <button key={h} type="button" data-kor-sik={h} disabled={korGonderiliyor}
                      onClick={() => korCevapla(h)}
                      className={`${sinif} text-left cursor-pointer hover:border-blue-400 hover:bg-blue-50`}>
                      <span className="font-bold mr-3 text-gray-600">({h})</span>
                      <LatexText>{s[`sik_${h.toLowerCase()}`]}</LatexText>
                    </button>
                  );
                }
                return (
                  <div key={h} className={sinif}>
                    <span className="font-bold mr-3 text-gray-600">({h})</span>
                    <LatexText>{s[`sik_${h.toLowerCase()}`]}</LatexText>
                    {dogru && <span className="text-green-600 font-bold text-xs ml-2">✓ DOĞRU</span>}
                    {korSecim && <span className={`font-bold text-xs ml-2 ${dogru ? 'text-green-700' : 'text-red-600'}`}>● kör cevabınız</span>}
                  </div>
                );
              })}
            </div>
          );
        })()}
        {korMod && (
          <div data-kor-mod className="mt-3 text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            🙈 <strong>Kör onay örneği:</strong> anahtar, çözüm ve QC kanıtı gizli. Önce kendi cevabınızı işaretleyin; ardından hepsi açılır ve cevabınız anahtarla karşılaştırılır.
            {korHata && <div className="mt-1 text-red-700">{korHata}</div>}
          </div>
        )}
        {!korMod && korBilgi && korBilgi.cevap && (() => {
          // Kör uyuşmazlık, kör cevaptan SONRA gelen QC ONAY'ıyla kapanır (2026-09-15, kullanıcı
          // onayı). Az önce verilen cevap (korSonuc) her QC izinden sonradır → kapalı olamaz.
          const kapali = !korSonuc && !korBilgi.uyustu && korUyusmazlikKapali(s);
          const renk = korBilgi.uyustu ? 'bg-green-50 border-green-200 text-green-900'
            : kapali ? 'bg-gray-50 border-gray-200 text-gray-800' : 'bg-red-50 border-red-200 text-red-900';
          return (
            <div data-kor-sonuc className={`mt-3 text-sm rounded-lg px-3 py-2 border ${renk}`}>
              {korBilgi.uyustu
                ? `✓ Kör cevabınız (${korBilgi.cevap}) anahtarla uyuştu.`
                : kapali
                ? `ⓘ Kör cevabınız (${korBilgi.cevap}) anahtardan (${s.dogru_cevap}) farklıydı; Cevap QC anahtarı sonradan doğruladı${s.cevap_qc.tarih ? ` (${formatTS(s.cevap_qc.tarih)})` : ''} — uyuşmazlık kapandı.`
                : `✗ Kör cevabınız (${korBilgi.cevap}) anahtardan (${s.dogru_cevap}) farklı — soru "Cevap QC: uyuşmazlık" kuyruğunda.`}
            </div>
          );
        })()}
      </div>

      {/* PRATIK COZUM — Sprint 3, 2026-05-15. 2026-07-29 (KURALLAR §17.7):
          pratik varsa detayli cozumun USTUNDE — ogretmen/ogrenci once sinav
          kisayolunu gorur, uzun detayli cozum asagida katlanir.
          Kör modda gizli (pratik çözüm cevabı açık eder). */}
      {s.pratik_cozum && !korMod && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 mb-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3 border-b border-amber-200 pb-2 flex-wrap">
            <h3 className="text-base font-semibold text-amber-800 m-0">⚡ Pratik Çözüm</h3>
            {s.pratik_cozum_sure_sn && (
              <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-semibold">
                ~{s.pratik_cozum_sure_sn} sn
              </span>
            )}
            {s.pratik_uretim_meta && s.pratik_uretim_meta.perspektif_secimi && (
              <span className="text-xs bg-white border border-amber-300 text-amber-800 px-2 py-0.5 rounded-full font-semibold" title="Seçilen öğretmen perspektifi">
                {s.pratik_uretim_meta.perspektif_secimi}
              </span>
            )}
            {/* Onay rozeti — 2026-08-26 (öğretmen isteği): VARSAYILAN ONAYLI.
                `pratik_onayli` NULL ise öğretmen henüz karar vermemiş demektir; kart
                artık "bekliyor/reddedildi" değil ONAYLI görünür. Yalnız AÇIK `false`
                (öğretmenin kendi reddi) kırmızı rozet basar. */}
            {s.pratik_onayli === false ? (
              <span className="text-xs text-red-700 font-semibold ml-auto">✗ Reddedildi</span>
            ) : s.pratik_onayli === true ? (
              <span className="text-xs text-green-700 font-semibold ml-auto">✓ Öğretmen onaylı</span>
            ) : (
              <span className="text-xs text-green-700 font-semibold ml-auto"
                    title="Öğretmen henüz karar vermedi — varsayılan onaylı">
                ✓ Onaylı <span className="text-gray-500 font-normal">(varsayılan)</span>
              </span>
            )}
          </div>

          {/* Kısa yol — her cümle ayrı paragraf (görsel hava için) */}
          <div className="text-[15px] text-gray-800 mb-3 space-y-2.5 max-w-prose">
            {s.pratik_cozum.split(/\n+/).filter(c => c.trim()).map((cumle, i) => (
              <p key={i} className="leading-relaxed m-0">
                <LatexText>{cumle.trim()}</LatexText>
              </p>
            ))}
          </div>

          {/* Püf noktaları */}
          {pufListesi(s.puf_noktalari).length > 0 && (
            <div className="space-y-2 mt-3 pt-3 border-t border-amber-200" data-testid="puf-noktalari">
              <div className="text-xs font-semibold text-amber-900 uppercase tracking-wide">💡 Püf Noktaları</div>
              {pufListesi(s.puf_noktalari).map((puf, i) => (
                <div key={i} className="bg-white rounded-lg p-3 text-sm border border-amber-100">
                  {puf.baslik && <div className="font-semibold text-amber-900 mb-1"><LatexText>{String(puf.baslik)}</LatexText></div>}
                  {puf.aciklama && <div className="text-gray-700 leading-6"><LatexText>{String(puf.aciklama)}</LatexText></div>}
                  {puf.formul && <div className="text-gray-700 leading-6 mt-1"><LatexText>{String(puf.formul)}</LatexText></div>}
                </div>
              ))}
            </div>
          )}

          {/* Öğretmen Onay/Reddet — 2026-08-26: butonlar HER ZAMAN görünür.
              Eski sürüm yalnız `durum==='onaylandi' && pratik_onayli===null`
              koşulunda çizdiriyordu; toplu yazılmış `false` değerler yüzünden
              butonlar kaybolmuş, öğretmen kararı geri alamıyordu. Artık mevcut
              seçim halkayla vurgulanır, iki yöne de geçiş serbesttir. */}
          {onPratikOnayla && (
            <div className="flex gap-2 mt-4 pt-3 border-t border-amber-200">
              <button onClick={() => onPratikOnayla(s.id, true)}
                title="Pratik çözümü onayla"
                className={`flex-1 py-2 rounded-lg text-white text-sm font-semibold cursor-pointer transition-colors ${
                  s.pratik_onayli === true
                    ? 'bg-green-600 ring-2 ring-green-800'
                    : 'bg-green-500 hover:bg-green-600'}`}>
                ✓ Pratik Onayla
              </button>
              <button onClick={() => onPratikOnayla(s.id, false)}
                title="Pratik çözümü reddet"
                className={`flex-1 py-2 rounded-lg text-white text-sm font-semibold cursor-pointer transition-colors ${
                  s.pratik_onayli === false
                    ? 'bg-red-600 ring-2 ring-red-800'
                    : 'bg-red-500 hover:bg-red-600'}`}>
                ✗ Pratik Reddet
              </button>
            </div>
          )}

          {/* Açık karar verilmişse varsayılana dön (NULL = varsayılan onaylı) */}
          {onPratikOnayla && s.pratik_onayli !== null && s.pratik_onayli !== undefined && (
            <div className="flex justify-end mt-3 pt-3 border-t border-amber-200">
              <button onClick={() => onPratikOnayla(s.id, null)}
                className="text-xs text-gray-600 hover:text-gray-900 underline cursor-pointer">
                Varsayılana dön (onaylı)
              </button>
            </div>
          )}
        </div>
      )}

      {/* COZUM — Sprint 3, paragraf bazli render + mavi cerceve (pratik kartla
          simetrik). 2026-07-29 (ogretmen #18555-18557): pratik varsa bu kart
          katlanabilir ve varsayilan KAPALI; pratik yoksa eski davranis (acik).
          Kör modda gizli (çözüm cevabı açık eder). */}
      {!korMod && (
      <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5 mb-4 shadow-sm">
        <div className={`flex items-center gap-2 flex-wrap ${cozumGoster ? 'mb-3 border-b border-blue-200 pb-2' : ''}`}>
          <h3 className="text-base font-semibold text-blue-800 m-0">📘 Çözüm</h3>
          {adimSayisiG ? (
            <span className="text-xs bg-blue-200 text-blue-900 px-2 py-0.5 rounded-full font-semibold">
              {adimSayisiG} adım
            </span>
          ) : null}
          {s.tahmini_sure_sn && (
            <span className="text-xs bg-white border border-blue-300 text-blue-800 px-2 py-0.5 rounded-full font-semibold" title="Tahmini çözüm süresi">
              ~{s.tahmini_sure_sn} sn
            </span>
          )}
          {pratikVar && (
            <button onClick={() => setDetayCozumAcik(a => !a)}
              className="ml-auto text-xs text-blue-700 underline cursor-pointer bg-transparent border-0 p-0 font-semibold">
              {cozumGoster ? 'Detaylı çözümü gizle ▲' : 'Detaylı çözümü göster ▼'}
            </button>
          )}
        </div>

        {/* Cümle/adım başına paragraf — overflow-x-auto: uzun formül kutu dışına taşmasın */}
        {cozumGoster && (
          <div className="text-[14px] text-gray-800 space-y-2.5 max-w-full overflow-x-auto break-words">
            {(() => {
              const ham = s.cozum_adimlari_latex || '';
              const sarmalanmis = ham;  // $$ sarma kaldirildi: $-suz duz metin cozumu display-math sanilip KaTeX Turkce hatasi veriyordu (#2562)
              const paragraflar = sarmalanmis.split(/\n+/).map(c => c.trim()).filter(Boolean);
              return paragraflar.map((cumle, i) => (
                <p key={i} className="leading-relaxed m-0">
                  <LatexText>{cumle}</LatexText>
                </p>
              ));
            })()}
          </div>
        )}

        {/* "Son Cevap" kutusu kaldirildi (2026-05-17, KURALLAR.md):
            cozum_son_cevap zaten cozum_adimlari sonunda gecer; ayri rozet
            soru cevabini ifsa eder + cozum kartinin sade goruntusunu bozar */}
      </div>
      )}

      {/* COZUM VIDEOSU — 2026-07-31. Cozum kartindan SONRA, metadata'dan ONCE:
          ogretmen once cozumun dogrulugunu okur, sonra sunumu degerlendirir.
          video_url MinIO'da anonim-okunur nesne (Authorization gonderilemez,
          presigned URL de suresi dolunca DB'deki link curur). Kör modda gizli. */}
      {s.video_url && !korMod && (
        <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-5 mb-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3 border-b border-indigo-200 pb-2 flex-wrap">
            <h3 className="text-base font-semibold text-indigo-800 m-0">🎬 Çözüm Videosu</h3>
            {s.video_sure_sn ? (
              <span className="text-xs bg-indigo-200 text-indigo-900 px-2 py-0.5 rounded-full font-semibold">
                {Math.floor(s.video_sure_sn / 60)}:{String(s.video_sure_sn % 60).padStart(2, '0')}
              </span>
            ) : null}
            {/* "N sahne" ve ses adi rozetleri KALDIRILDI (ogretmen, 2026-07-31):
                uretim ayrintisi ogretmeni ilgilendirmiyor, karti kalabaliklastiriyor.
                Bu bilgiler video_uretim_meta'da duruyor, gerektiginde oradan okunur. */}
            {s.video_onayli === true && (
              <span className="text-xs text-green-700 font-semibold ml-auto">✓ Öğretmen onaylı</span>
            )}
            {s.video_onayli === false && (
              <span className="text-xs text-red-700 font-semibold ml-auto">✗ Reddedildi</span>
            )}
            {(s.video_onayli === null || s.video_onayli === undefined) && (
              <span className="text-xs text-indigo-700 font-semibold ml-auto">○ Onay bekliyor</span>
            )}
          </div>

          <video
            controls
            preload="metadata"
            playsInline
            poster={s.video_kapak_url || undefined}
            src={s.video_url}
            className="w-full rounded-lg bg-black"
            style={{ maxHeight: 520 }}
          >
            Tarayıcınız video oynatmayı desteklemiyor.
          </video>

          {onVideoOnayla && s.durum === 'onaylandi' && (s.video_onayli === null || s.video_onayli === undefined) && (
            <div className="flex gap-2 mt-4 pt-3 border-t border-indigo-200">
              <button onClick={() => onVideoOnayla(s.id, true)}
                className="flex-1 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-semibold cursor-pointer transition-colors">
                ✓ Videoyu Onayla
              </button>
              <button onClick={() => onVideoOnayla(s.id, false)}
                className="flex-1 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold cursor-pointer transition-colors">
                ✗ Videoyu Reddet
              </button>
            </div>
          )}
          {onVideoOnayla && s.durum === 'onaylandi' && s.video_onayli !== null && s.video_onayli !== undefined && (
            <div className="flex justify-end mt-3 pt-3 border-t border-indigo-200">
              <button onClick={() => onVideoOnayla(s.id, null)}
                className="text-xs text-gray-600 hover:text-gray-900 underline cursor-pointer">
                Onay durumunu sıfırla
              </button>
            </div>
          )}
        </div>
      )}

      {/* Video uretim asamasinda / hatali ise durum bildir */}
      {!s.video_url && (s.video_durum === 'kuyrukta' || s.video_durum === 'uretiliyor') && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4 text-sm text-gray-600">
          🎬 Çözüm videosu hazırlanıyor…
        </div>
      )}
      {!s.video_url && s.video_durum === 'hata' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-sm text-red-700">
          🎬 Çözüm videosu üretilemedi
          {s.video_uretim_meta && s.video_uretim_meta.hata
            ? ` — ${String(s.video_uretim_meta.hata).slice(0, 160)}`
            : ''}
        </div>
      )}

      {/* METADATA */}
      <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
        <h3 className="text-base font-semibold text-blue-700 mt-0 mb-3 border-b border-gray-100 pb-2">Detaylar</h3>
        <div className="grid grid-cols-2 gap-2 text-[13px] text-gray-500">
          <div>Model: <strong>{s.kullanilan_model}</strong></div>
          <div>Maliyet: <strong>${s.uretim_maliyeti_usd}</strong></div>
          <div>Adım Sayısı: <strong>{adimSayisiG || '—'}</strong></div>
          <div>Tahmini Süre: <strong>{s.tahmini_sure_sn ? `${s.tahmini_sure_sn}sn` : '—'}</strong></div>
          {/* Cevap QC (2026-09-15): eski "SymPy: ✓ Doğrulandı" satırı KALDIRILDI — bayrak hiç
              SymPy koşmadan basılıyordu (#974). Değer denetim_izi CEVAP-QC izinden gelir. */}
          {!korMod && (
            <div data-cevap-qc>Cevap QC: <strong>{(() => {
              const q = s.cevap_qc;
              if (!q) return '— denetlenmedi';
              const ad = {
                ONAY: '✓ Onay',
                DUZELTME: '✎ QC düzeltti — yeniden denetim bekliyor',
                RED: '✗ Hatalı — revizede',
                'KOD-KOSMADI': '? Kanıt koşmadı — elle bakılmalı',
              };
              // Satılmış soruya DOKUNULMAZ (kullanıcı kararı: yalnız raporla) — "revizede" /
              // "yeniden denetim" demek yanlış olurdu (2026-09-15, kullanıcı onayı).
              const satilmisAd = {
                ONAY: '✓ Onay',
                DUZELTME: '✎ düzeltme gerekli — yalnız raporlandı',
                RED: '✗ hatalı bulundu — yalnız raporlandı',
                'KOD-KOSMADI': '? Kanıt koşmadı — elle bakılmalı',
              };
              const satilmis = String(q.sonuc).startsWith('SATILMIS-RAPOR:');
              const ic = satilmis ? String(q.sonuc).split(':')[1] : String(q.sonuc);
              const etiket = (satilmis ? satilmisAd : ad)[ic] || ic;
              return `${satilmis ? 'Satılmış · ' : ''}${etiket}${q.tarih ? ` (${formatTS(q.tarih)})` : ''}`;
            })()}</strong></div>
          )}
          <div>ÖSYM Skoru: <strong>{s.osym_uslup_skoru || '—'}</strong></div>
          <div>Parmak İzi: <strong>{s.parmak_izi ? s.parmak_izi.slice(0,12)+'...' : '—'}</strong></div>
          <div>{kazanimEtiketi(s)} Kodu: <strong>{s.kazanim_kodu || '—'}</strong></div>
          <div>Müfredat: <strong>{mufredatAdi(s.kazanim_kodu, s.ders)}</strong></div>
          <div>Alt Konu: <strong>{s.alt_konu || '—'}</strong></div>
          <div>Zorluk Güven: <strong>{s.zorluk_guven ? parseFloat(s.zorluk_guven).toFixed(2) : '—'}</strong></div>
          <div>Oluşturma: <strong>{formatTS(s.olusturulma_zamani)}</strong></div>
          <div>Son İşlem: <strong className={guncellemeYakinMi(s.guncelleme_zamani) ? 'text-blue-700' : ''}>{formatTS(s.guncelleme_zamani)}</strong></div>
        </div>
        {/* Cevap QC (2026-09-15, kullanıcı istekleri): "QC'ye gönder" (yalnız admin) + kanıt. Kör
            modda ikisi de ÇİZİLMEZ — anahtarı ele verir. */}
        {!korMod && <CevapQcIstek key={`i-${s.id}`} soru={s} onIstek={onCevapQcIstek} />}
        {!korMod && <CevapQcKanit key={s.id} soru={s} onKanit={onCevapQcKanit} Metin={LatexText} />}
      </div>

      {/* RED BİLGİSİ — sadece reddedilmiş sorularda */}
      {s.durum === 'reddedildi' && (s.red_nedeni || s.ogretmen_yorumu) && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 mb-4">
          <h3 className="text-base font-semibold text-red-700 mt-0 mb-3 border-b border-red-200 pb-2">✗ Red Bilgisi</h3>
          {s.red_nedeni && (
            <div className="mb-2 text-sm">
              <span className="text-gray-500">Neden:</span>{' '}
              <span className="font-semibold text-red-800">{redNedeniEtiket(s.red_nedeni)}</span>
            </div>
          )}
          {s.ogretmen_yorumu && (
            <div className="text-sm">
              <span className="text-gray-500">Öğretmen notu:</span>{' '}
              <span className="text-gray-800 italic">"{s.ogretmen_yorumu_temiz ?? s.ogretmen_yorumu}"</span>
            </div>
          )}
        </div>
      )}

      {/* DEGERLENDIRME */}
      <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
        {/* Durum rozeti BUTONLARIN yaninda da gorunur (ogretmen 2026-08-16): kart
            basligindaki rozet (yukarida) onay sonrasi dogru guncelleniyor ama uzun
            kartin EN USTUNDE — ogretmen onaydan sonra kartin ALTINDA duruyor ve
            hicbir degisiklik gormuyordu. Grup gorunumunde "hangi soru kaldi" bunu
            bir bakista gosterir. */}
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2 mb-3">
          <h3 className="text-base font-semibold text-blue-700 mt-0 mb-0">Öğretmen Değerlendirmesi</h3>
          <span className="text-white px-2.5 py-1 rounded text-xs font-semibold whitespace-nowrap"
                style={{ backgroundColor: durumRenk(s.durum) }}>
            {durumEtiket(s.durum)}{s.zorluk_puan != null ? ` · ${s.zorluk_puan}/10` : ''}
          </span>
        </div>
        <div className="mb-3">
          {/* Bantlar ZORLUK_SEVIYELERI'nden çizilir — lejant kodla asla ayrışmaz.
              1 bir zorluk seviyesi DEĞİL, "hatalı soru" işaretidir (öğretmen 2026-08-31). */}
          <div className="text-sm mb-2 flex items-center gap-x-3 gap-y-1 flex-wrap">
            <span>Zorluk puanı:</span>
            {ZORLUK_SEVIYELERI.map(sv => (
              <span key={sv.ad} className="inline-flex items-center gap-1 text-xs text-gray-600">
                <span className="w-2.5 h-2.5 rounded-full inline-block shrink-0" style={{ backgroundColor: sv.renk }} />
                {sv.min === sv.max ? sv.min : `${sv.min}-${sv.max}`} {sv.ad}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {[1,2,3,4,5,6,7,8,9,10].map(p => {
              const active = puan === p;
              const activeCls = zorlukSeviye(p).aktifSinif;
              return (
                <button key={p} onClick={() => setPuan(p)} title={puanBaslik(p)}
                  className={`w-9 h-9 rounded-full border-2 font-bold text-base cursor-pointer transition-colors ${active ? activeCls : 'bg-white border-gray-300 hover:border-blue-400 text-gray-700'}`}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>
        {/* ÖĞRETMEN YORUMU + SESLİ YAZDIRMA (dikte, tr-TR).
            ARA (interim) SONUÇ KUTUYA YAZILMAZ, altta gri önizlemede akar.
            Gerekçe: `yorum` doğrudan onOnayla(...)/onPuanKaydet ile POST edilen
            değerdir; yarım tanınmış metin kutuya girerse öğretmen cümlenin
            ortasında "Onayla"ya basınca DB'ye bozuk yorum yazılır. Ayrıca ara
            sonuç her olayda TÜMÜYLE değişir — controlled value'ya karıştırılınca
            öğretmenin aynı anda klavyeyle yazdığı metinle çakışır. */}
        <div className="relative mb-3" onKeyDown={sesTusu}>
          <textarea
            ref={yorumRef}
            placeholder="Öğretmen yorumu (opsiyonel)..."
            value={yorum}
            onChange={e => setYorum(e.target.value)}
            aria-describedby={sesDurumId}
            className="w-full min-h-[80px] p-3 pr-12 rounded-lg border border-gray-300 text-sm font-serif resize-y focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <MikrofonButonu
            className="absolute top-2 right-2"
            dinliyor={ses.dinliyor}
            destekli={ses.destekli}
            destekMesaji={ses.destekMesaji}
            onTikla={ses.degistir}
          />
          {/* Durum satırı — toast yok, satır içi. Yükseklik REZERVE (min-h) ki
              dikte başlayınca alttaki butonlar aşağı zıplamasın. */}
          <div id={sesDurumId} aria-live="polite" className="min-h-[1.15rem] mt-1 text-xs leading-tight">
            {ses.mesaj ? (
              <span className={ses.mesaj.tip === 'hata' ? 'text-red-600' : 'text-amber-600'}>
                {ses.mesaj.metin}
              </span>
            ) : ses.araMetin ? (
              <span className="text-gray-400 italic">{ses.araMetin}…</span>
            ) : ses.dinliyor ? (
              <span className="text-gray-400">Dinleniyor — konuşun (durdurmak için Esc)</span>
            ) : !ses.destekli ? (
              <span className="text-gray-400">{ses.destekMesaji}</span>
            ) : null}
          </div>
        </div>
        {Array.isArray(s.sistem_notlari) && s.sistem_notlari.length > 0 && (
          <div className="mb-3 border border-gray-200 rounded-lg bg-gray-50">
            <button type="button" onClick={() => setSistemAcik(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-600 bg-transparent border-0 cursor-pointer hover:text-gray-800">
              <span>🤖 Sistem notları ({s.sistem_notlari.length})</span>
              <span className="text-xs">{sistemAcik ? '▲' : '▼'}</span>
            </button>
            {sistemAcik && (
              <div className="px-3 pb-3 flex flex-col gap-2">
                {s.sistem_notlari.map((n, i) => (
                  <div key={i} className="text-xs bg-white border border-gray-200 rounded p-2">
                    <div className="font-mono text-gray-700 whitespace-pre-wrap break-words">{n.metin}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <select value={redNedeni} onChange={e => setRedNedeni(e.target.value)}
          className="w-full p-2.5 rounded-lg border border-gray-300 text-sm mb-4 font-serif focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Red nedeni seçin (red için)</option>
          {redNedeniOptions.map(key => (
            <option key={key} value={key}>{redNedeniEtiket(key)}</option>
          ))}
        </select>
        {/* PUAN GONDERIMI (ogretmen 2026-08-12 "onay veremiyorum"):
            `puan` state'i puanlanmamis soruda 0'dir; backend `/onayla` ucu
            puan icin 1-10 sarti kosuyor -> 0 gonderilince HTTP 400
            ("puan 1-10 araliginda olmalidir") ve onay/red SESSIZCE dusuyordu.
            0 = "puan verilmedi" demektir -> null gonderilir (backend null'i
            kabul eder, zorluk_puan NULL kalir). Puan verilmeden onaylandigi
            buton etiketinde ACIKCA yazar, sessiz kayip olmaz. */}
        {/* SATILMIŞ SORU KİLİDİ (2026-09-14): onay/puan/pratik yazımı sunucuda 409
            döner; App.js "yine de değiştirilsin mi?" onayıyla satis_override gönderir. */}
        {satildiMi(s.satis) && (
          <div data-satis-kilit className="mb-2 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5"
               title={satisRozetBaslik(s.satis)}>
            🔒 İçerik kilitli (satıldı) — değişiklik için ayrıca onay istenir.
          </div>
        )}
        {korPuanBilgi && (
          <div data-kor-puan-bilgi className="mb-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            🙈 Kör puanınız {korPuanBilgi.puan}/10
            {korPuanBilgi.ajan_puan != null && ` · ajan ${korPuanBilgi.ajan_puan}/10 (fark ${korPuanBilgi.ajan_puan - korPuanBilgi.puan > 0 ? '+' : ''}${korPuanBilgi.ajan_puan - korPuanBilgi.puan})`}
            {' — ölçüm içindir; sorunun puanı değişmedi.'}
          </div>
        )}
        {korPuanHata && (
          <div className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">{korPuanHata}</div>
        )}
        {korPuanMod ? (
        <div className="flex gap-3">
          {/* Kör puan modunda yalnız puan kaydedilir (ölçüm); Onayla/Puan Kaydet zorluk_puan'ı
              ajanın puanının üstüne yazardı. Reddet açık: soru hatalıysa öğretmen reddeder. */}
          <button onClick={korPuanKaydet}
            disabled={!puan || korPuanGonderiliyor}
            data-kor-puan-kaydet
            title="Ajanın puanını görmeden verdiğiniz puan yalnız ölçüm için kaydedilir; yorum kaydedilmez"
            className={`flex-[2] py-3.5 rounded-lg text-white text-base font-bold transition-colors ${puan && !korPuanGonderiliyor ? 'bg-amber-600 hover:bg-amber-700 cursor-pointer' : 'bg-amber-300 cursor-not-allowed'}`}>
            {korPuanGonderiliyor ? 'Kaydediliyor…' : `🙈 Kör puanı kaydet${puan ? ` (${puan}/10)` : ''}`}
          </button>
          <button onClick={() => onOnayla(s.id, false, yorum, null, redNedeni || null)}
            disabled={redKilitli}
            className={`flex-1 py-3.5 rounded-lg text-white text-base font-bold transition-colors ${redKilitli ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 cursor-pointer'}`}>
            {redKilitli ? '✗ Reddedildi' : '✗ Reddet'}
          </button>
        </div>
        ) : (
        <div className="flex gap-3">
          {/* Kör modda "Onayla" pasif: önce kör cevap. "Reddet" açık kalır — figür bozukluğu
              gibi cevaptan bağımsız bir nedenle red kör cevap gerektirmez. */}
          <button onClick={() => onOnayla(s.id, true, yorum, puan || null)}
            disabled={onayKilitli || korMod}
            data-onayla
            title={korMod
              ? 'Kör onay örneği — önce yukarıdan kendi cevabınızı işaretleyin'
              : onayKilitli
              ? 'Bu soru zaten onaylandı — puanı "★ Puan Kaydet" ile değiştirebilirsiniz'
              : (puan ? `Puan ${puan}/10 ile onayla` : 'Puan vermeden onayla (puan sonra da verilebilir)')}
            className={`flex-1 py-3.5 rounded-lg text-white text-base font-bold transition-colors ${(onayKilitli || korMod) ? 'bg-green-300 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 cursor-pointer'}`}>
            {onayKilitli ? '✓ Onaylandı' : korMod ? '🙈 Önce kör cevap' : `✓ Onayla${puan ? ` (${puan}/10)` : ' (puansız)'}`}
          </button>
          <button onClick={() => onPuanKaydet && onPuanKaydet(s.id, puan)}
            disabled={!puan}
            className={`flex-1 py-3.5 rounded-lg text-white text-base font-bold transition-colors ${puan ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer' : 'bg-gray-300 cursor-not-allowed'}`}>
            ★ Puan Kaydet
          </button>
          <button onClick={() => onOnayla(s.id, false, yorum, puan || null, redNedeni || null)}
            disabled={redKilitli}
            title={redKilitli ? 'Bu soru zaten reddedildi' : undefined}
            className={`flex-1 py-3.5 rounded-lg text-white text-base font-bold transition-colors ${redKilitli ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600 cursor-pointer'}`}>
            {redKilitli ? '✗ Reddedildi' : '✗ Reddet'}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// LGS soruları (lgs_* ders) aynı Maarif rozetini taşır ama hedef sınav LGS'dir.
function hedefSinav(ders) { return String(ders || '').startsWith('lgs_') ? '2028+ LGS' : '2028+ YKS'; }
function mufredatEtiketi(kod, ders) {
  if (!kod) return null;
  if (maarifKazanimMi(kod) || String(ders || '').startsWith('lgs_')) {
    return (
      <span
        className="bg-emerald-50 text-emerald-800 border border-emerald-300 px-3 py-1 rounded-full text-[13px] font-semibold"
        title={`Maarif 2024 müfredatı — ${hedefSinav(ders)} hedefli`}
      >
        Maarif 2024 · {hedefSinav(ders)}
      </span>
    );
  }
  if (/^\d+\.\d+/.test(kod)) {
    return (
      <span
        className="bg-amber-50 text-amber-800 border border-amber-300 px-3 py-1 rounded-full text-[13px] font-semibold"
        title="MEB 2018 müfredatı — 2026-2027 YKS"
      >
        MEB 2018 · 2026-27 YKS
      </span>
    );
  }
  return null;
}
function mufredatAdi(kod, ders) {
  if (!kod) return '—';
  if (maarifKazanimMi(kod) || String(ders || '').startsWith('lgs_')) return `Maarif 2024 (${hedefSinav(ders)})`;
  if (/^\d+\.\d+/.test(kod)) return 'MEB 2018 (2026-27 YKS)';
  return '—';
}
function zorlukRenk(z) { return { kolay: '#4CAF50', orta: '#FF9800', zor: '#F44336', uzman: '#9C27B0' }[z] || '#757575'; }
function durumRenk(d) { return { uretildi: '#2196F3', judge_gecti: '#FF9800', duplicate_kontrol_edildi: '#8BC34A', onay_bekliyor: '#FFC107', onaylandi: '#4CAF50', reddedildi: '#F44336', tikz_hatali: '#E91E63', judge_bekliyor: '#9E9E9E' }[d] || '#757575'; }
function durumEtiket(d) { return { uretildi: 'Üretildi', judge_gecti: 'Değerlendirildi', duplicate_kontrol_edildi: 'Doğrulandı', onay_bekliyor: 'Onay Bekliyor', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi', tikz_hatali: 'TikZ Hatalı', judge_bekliyor: 'Judge Bekliyor', sympy_dogrulandi: 'SymPy OK' }[d] || d; }
function formatTS(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function guncellemeYakinMi(iso) {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) < 24 * 60 * 60 * 1000;
}
